import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  addToDemoExcluded,
  initializeExcludedBaseAssets,
  selectDemoEndTimestamp,
  selectDemoHasInitialized,
  selectDemoStartTimestamp,
  selectIsDemo,
} from '../store/slices/demo';
import { selectSubscriptionSummaries } from '../store/slices/subscription';
import { useDemoRebasedSubscriptions } from '../store/hooks/useDemoRebasedSubscriptions';

/**
 * Auto-disables outlier subscriptions in demo mode so the dashboard isn't
 * dominated by one or two positions.
 *
 *  - First time demo subs land, any asset weighing >= 50% of total portfolio
 *    value is auto-excluded. `hasInitialized` flips so a subsequent settings
 *    change isn't clobbered.
 *  - When the user changes the date window, newly-too-big assets get
 *    auto-disabled additively (previously-disabled subs are never re-enabled).
 *    A ref of the last-seen window prevents the effect from running on every
 *    excluded-list update, which would loop.
 *
 * In real-user mode the hook is a no-op. Mounting it from Overview keeps that
 * screen free of demo-specific logic.
 */
const useDemoOutlierAutoDetect = (): void => {
  const dispatch = useDispatch();
  const isDemo = useSelector(selectIsDemo);
  const demoHasInitialized = useSelector(selectDemoHasInitialized);
  const rawSummaries = useSelector(selectSubscriptionSummaries);
  const demoStart = useSelector(selectDemoStartTimestamp);
  const demoEnd = useSelector(selectDemoEndTimestamp);
  const { visible: rebasedVisible } = useDemoRebasedSubscriptions();

  useEffect(() => {
    if (!isDemo || demoHasInitialized || !rawSummaries.length) return;
    const totalValue = rawSummaries.reduce(
      (s, sub) => s + (sub.totalBalanceInStable || 0),
      0,
    );
    if (totalValue <= 0) return;
    const outliers = rawSummaries
      .filter((sub) => (sub.totalBalanceInStable || 0) / totalValue >= 0.5)
      .map((sub) => sub.baseAsset);
    dispatch(initializeExcludedBaseAssets(outliers));
  }, [dispatch, isDemo, demoHasInitialized, rawSummaries]);

  const lastSeenDateWindow = useRef<{
    start: number | null;
    end: number | null;
  } | null>(null);
  useEffect(() => {
    if (!isDemo) return;
    // Wait for the first auto-init to seed `hasInitialized=true` before we
    // start the re-detect loop — otherwise we'd race the initialise path.
    if (!demoHasInitialized) return;
    const last = lastSeenDateWindow.current;
    if (last && last.start === demoStart && last.end === demoEnd) return;
    lastSeenDateWindow.current = { start: demoStart, end: demoEnd };
    if (!rebasedVisible.length) return;
    const includedTotal = rebasedVisible.reduce(
      (s, sub) => s + (sub.totalBalanceInStable || 0),
      0,
    );
    if (includedTotal <= 0) return;
    const outliers = rebasedVisible
      .filter((sub) => (sub.totalBalanceInStable || 0) / includedTotal >= 0.5)
      .map((sub) => sub.baseAsset);
    if (outliers.length) dispatch(addToDemoExcluded(outliers));
  }, [
    dispatch,
    isDemo,
    demoHasInitialized,
    demoStart,
    demoEnd,
    rebasedVisible,
  ]);
};

export default useDemoOutlierAutoDetect;
